import type { ExampleResult, ExerciseDto, ExerciseExample, HintBlock } from '@pebblecode/shared';
import type { ReactNode } from 'react';

import { Icon } from '../components/Icon.js';
import { Markup } from '../components/Markup.js';
import { useT, type Messages } from '../i18n/index.js';

const CHIP_CATEGORY: Record<HintBlock, string> = {
  while: 'loops',
  repeat: 'loops',
  for: 'loops',
  break: 'loops',
  if: 'logic',
  ifElse: 'logic',
  compare: 'logic',
  and: 'logic',
  mod: 'math',
  intDiv: 'math',
  change: 'variables',
  join: 'text',
  read: 'io',
  print: 'io',
  list: 'lists',
  table: 'lists',
  index: 'lists',
  length: 'text',
  letter: 'text',
  code: 'text',
  case: 'text',
  find: 'text',
  part: 'text',
};

export function Difficulty({ level, label }: { level: number; label: string }) {
  return (
    <span className="dots" role="img" aria-label={label}>
      {[1, 2, 3].map((i) => (
        <i key={i} className={i <= level ? 'on' : ''} />
      ))}
    </span>
  );
}

/**
 * Example text exactly as the checker sees it: every line on its own line, so an exercise that
 * wants three lines never looks like one line with spaces. Empty output stays visible.
 */
function ExampleText({ value, label }: { value: string; label: string }) {
  return (
    <div className="example-field">
      <span className="eyebrow">{label}</span>
      <pre className="example-value">{value.replace(/\s+$/, '') || ' '}</pre>
    </div>
  );
}

function ExampleCard({
  index,
  example,
  result,
  t,
}: {
  index: number;
  example: ExerciseExample;
  result: ExampleResult | undefined;
  t: Messages;
}) {
  const status = result?.status;
  return (
    <li className={`example-card${status ? ` example-${status === 'ok' ? 'ok' : 'bad'}` : ''}`}>
      <div className="example-head">
        <span className="example-name">{t.exercise.exampleN(index + 1)}</span>
        {status && (
          <span className={`example-verdict${status === 'ok' ? ' tone-ok' : ' tone-error'}`}>
            <Icon name={status === 'ok' ? 'check' : 'close'} size={13} strokeWidth={2.6} />
            {t.exercise.exampleStatus[status]}
          </span>
        )}
      </div>
      <ExampleText label={t.exercise.input} value={example.input} />
      <ExampleText label={t.exercise.output} value={example.output} />
      {result && status !== 'ok' && (
        <ExampleText label={t.exercise.yourOutput} value={result.actual} />
      )}
    </li>
  );
}

interface Props {
  exercise: ExerciseDto | null;
  solved: boolean;
  /** Per-example results of the last finished run; null before the first run or after an edit. */
  results?: ExampleResult[] | null;
  /** Extra content between the statement and the tutor area (e.g. live activity). */
  children?: ReactNode;
  /** The tutor quick actions at the bottom of the panel. */
  tutor?: ReactNode;
}

export function ExercisePanel({ exercise, solved, results, children, tutor }: Props) {
  const t = useT();
  return (
    <aside className="side-panel exercise-panel">
      {/* Focusable so the text can be scrolled from the keyboard when it does not fit. */}
      <div
        className="exercise-scroll"
        role="region"
        aria-label={exercise?.title ?? t.exercise.freeTitle}
        tabIndex={0}
      >
        {exercise ? (
          <>
            <div className="exercise-eyebrow">
              <span className="eyebrow">
                {exercise.custom
                  ? t.teach.byTeacher
                  : t.exercise.position(exercise.position, exercise.total)}
              </span>
              <Difficulty
                level={exercise.difficulty}
                label={t.exercise.difficulty(exercise.difficulty)}
              />
            </div>
            <h1 className="exercise-title">{exercise.title}</h1>
            {solved && (
              <p className="solved-badge">
                <Icon name="check" size={14} strokeWidth={2.6} /> {t.exercise.solved}
              </p>
            )}
            <div className="exercise-statement">
              <Markup text={exercise.statement} />
            </div>
            <div className="examples">
              <div className="examples-head">
                <span className="eyebrow">{t.exercise.examples}</span>
                <span className="examples-note">{t.exercise.examplesNote}</span>
              </div>
              <ul className="example-list">
                {exercise.examples.map((ex, i) => (
                  <ExampleCard key={i} index={i} example={ex} result={results?.[i]} t={t} />
                ))}
              </ul>
            </div>
            {exercise.hintBlocks.length > 0 && (
              <div className="hint-blocks">
                <span className="eyebrow">{t.exercise.blocksYouMightNeed}</span>
                <div className="chips">
                  {exercise.hintBlocks.map((h) => (
                    <span key={h} className={`chip chip-${CHIP_CATEGORY[h]}`}>
                      {t.chips[h]}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <h1 className="exercise-title">{t.exercise.freeTitle}</h1>
            <div className="exercise-statement">
              <p>{t.exercise.freeText}</p>
            </div>
          </>
        )}
        {children}
      </div>
      {tutor}
    </aside>
  );
}
